// Setup → Workstations (docs/ringcentral/WORKSTATIONS.md). Each SOC workstation has its own RingCentral extension (the
// headset on its PC and its desk phone); agents have none and sit at a workstation for their shift.
//   admins: add and edit workstations (label, extension, active) and sign each one in to RingCentral once, as the
//           workstation's RingCentral user (the pop-up; the server keeps the token)
//   supervisors: see who sits where and release a seat (moving someone), with a reason
//   admins, with PCs recognised (docs/ringcentral/WORKSTATION-DEVICES.md): assign each SOC PC to its desk once, or block
//           one; whoever logs in at an assigned PC is seated at its desk
// Everything here is audited server-side.

import { useEffect, useState } from 'react';
import { api, errorText, type Workstation, type WorkstationDevice } from '../../api.ts';
import { Icon } from '../../components/Icon.tsx';
import { Card, Empty, ErrorBox, Loading } from '../../components/ui.tsx';
import { useLoad } from '../../hooks.ts';
import { useSession } from '../../session.tsx';
import { useStreamEvent } from '../../stream.tsx';
import { rcSignIn } from '../../telephony/rc-signin.ts';
import { EXTENSION_NUMBER_HINT, looksLikeExtensionNumber } from './extension-id.ts';

const when = (iso: string) => new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

function DeviceRow({ d, desks, isHere, onChanged }: { d: WorkstationDevice; desks: Workstation[]; isHere: boolean; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const run = async (body: { workstationId?: string | null; blocked?: boolean }) => {
    setBusy(true);
    setErr(null);
    try {
      await api.patchWorkstationDevice(d.id, body);
      onChanged();
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  // A desk can have one PC: offer the free ones and this PC's own.
  const options = desks.filter((w) => w.active && (!w.device || w.device.id === d.id));
  return (
    <tr className={d.blocked ? 'dim' : undefined}>
      <td>
        <b>{d.name ?? 'unnamed PC'}</b>
        {isHere && <span className="pill ok tiny-pill">this computer</span>}
        <div className="small muted mono" title="Entra device ID">
          {d.id}
        </div>
      </td>
      <td>
        {d.blocked ? (
          <span className="muted">blocked</span>
        ) : (
          <select aria-label={`Desk for ${d.name ?? d.id}`} value={d.workstation?.id ?? ''} disabled={busy} onChange={(e) => void run({ workstationId: e.target.value || null })}>
            <option value="">Not assigned</option>
            {options.map((w) => (
              <option key={w.id} value={w.id}>
                {w.label}
              </option>
            ))}
          </select>
        )}
      </td>
      <td className="small">
        {when(d.lastSeenAt)}
        {d.lastSeenBy && <span className="muted"> · {d.lastSeenBy}</span>}
      </td>
      <td className="small">{d.certExpires ? new Date(d.certExpires).toLocaleDateString() : '—'}</td>
      <td>
        <button type="button" className="btn sm" disabled={busy} onClick={() => void run({ blocked: !d.blocked })} title={d.blocked ? 'Let this PC take its desk again' : 'Lost, replaced or out of service: never match it to a desk'}>
          {d.blocked ? 'Unblock' : 'Block'}
        </button>
        {err && <div className="small bad-t">{err}</div>}
      </td>
    </tr>
  );
}

/** The SOC PCs seen with a device certificate, and their desks (admins). */
function Computers({ desks, onChanged }: { desks: Workstation[]; onChanged: () => void }) {
  const load = useLoad((signal) => api.workstationDevices(signal), []);
  const reload = () => {
    load.reload();
    onChanged();
  };
  if (load.error && !load.data) return <ErrorBox error={load.error} onRetry={load.reload} title="Could not load computers" />;
  if (!load.data) return <Loading label="Loading computers…" />;
  const { recognising, here, devices } = load.data;
  return (
    <Card title="Computers">
      {!recognising ? (
        <p className="note">
          Computer recognition is off: agents pick their desk in the phone panel. It turns on with the SOC PCs' Intune device certificates and <span className="mono">WORKSTATION_DEVICE_CA</span> (docs/ringcentral/WORKSTATION-DEVICES.md).
        </p>
      ) : (
        <>
          <p className="note">
            Each SOC PC is recognised by its Intune device certificate. Assign it to the desk it stands at, once: whoever logs in there is then seated at that desk, with its phone. A PC appears here the first time someone opens the console on it.
            {here ? '' : ' This browser is not on a recognised SOC PC.'}
          </p>
          {devices.length === 0 ? (
            <Empty>No SOC PCs seen yet. Open the console on each one once.</Empty>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Computer</th>
                  <th>Desk</th>
                  <th>Last seen</th>
                  <th>Certificate expires</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {devices.map((d) => (
                  <DeviceRow key={`${d.id}:${d.workstation?.id}:${d.blocked}`} d={d} desks={desks} isHere={here?.deviceId === d.id} onChanged={reload} />
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </Card>
  );
}

function Row({ w, isAdmin, ringcentral, onChanged }: { w: Workstation; isAdmin: boolean; ringcentral: boolean; onChanged: () => void }) {
  const [label, setLabel] = useState(w.label);
  const [ext, setExt] = useState(w.extensionId ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      onChanged();
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const dirty = label.trim() !== w.label || (ext.trim() || null) !== w.extensionId;
  const release = () => {
    const reason = window.prompt(`Release ${w.label} from ${w.seated?.name}? Say why (at least 5 characters):`);
    if (reason) void run(() => api.releaseWorkstation(w.id, reason));
  };
  return (
    <tr className={w.active ? undefined : 'dim'}>
      <td>
        {isAdmin ? <input aria-label="Label" value={label} onChange={(e) => setLabel(e.target.value)} maxLength={60} disabled={!w.active} /> : w.label}
      </td>
      <td>
        {isAdmin ? <input aria-label="RingCentral extension ID" className="mono rcext" value={ext} onChange={(e) => setExt(e.target.value.replace(/\D/g, ''))} placeholder="extension ID" disabled={!w.active} /> : <span className="mono">{w.extensionId ?? '—'}</span>}
        {isAdmin && looksLikeExtensionNumber(ext) && <div className="note warn-t small">{EXTENSION_NUMBER_HINT}</div>}
      </td>
      <td>
        {w.seated ? (
          <>
            {w.seated.name} <span className="small muted">since {new Date(w.seated.since).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</span>{' '}
            <button type="button" className="btn sm" disabled={busy} onClick={release} title="Move this agent off the workstation">
              Release
            </button>
          </>
        ) : (
          <span className="muted">{w.active ? 'free' : 'inactive'}</span>
        )}
      </td>
      {isAdmin && ringcentral && (
        <td>
          {w.signedIn ? <span className="pill ok tiny-pill">signed in</span> : <span className="pill warn tiny-pill">not signed in</span>}{' '}
          {w.active && w.extensionId && (
            <button type="button" className="btn sm" disabled={busy} onClick={() => void run(() => rcSignIn(() => api.workstationRcLogin(w.id)))} title="Sign in as this workstation's RingCentral user">
              {w.signedIn ? 'Sign in again' : 'Sign in'}
            </button>
          )}{' '}
          {w.signedIn && (
            <button type="button" className="btn sm" disabled={busy} onClick={() => void run(() => api.workstationRcLogout(w.id))}>
              Sign out
            </button>
          )}
        </td>
      )}
      {isAdmin && (
        <td>
          {dirty && (
            <button type="button" className="btn sm pri" disabled={busy || !label.trim()} onClick={() => void run(() => api.patchWorkstation(w.id, { label: label.trim(), extensionId: ext.trim() || null }))}>
              Save
            </button>
          )}{' '}
          <button type="button" className="btn sm" disabled={busy} onClick={() => void run(() => api.patchWorkstation(w.id, { active: !w.active }))}>
            {w.active ? 'Deactivate' : 'Reactivate'}
          </button>
          {err && <div className="small bad-t">{err}</div>}
        </td>
      )}
      {!isAdmin && err && (
        <td>
          <span className="small bad-t">{err}</span>
        </td>
      )}
    </tr>
  );
}

export function WorkstationsEditor() {
  const s = useSession();
  const isAdmin = s.isAdmin;
  const ringcentral = s.config.telephony.provider === 'ringcentral';
  const load = useLoad((signal) => api.workstations(isAdmin, signal), [isAdmin]);
  useStreamEvent('softphone', (d) => {
    if ((d as { type?: string } | null)?.type === 'seat') load.reload();
  });
  // Agents are seated as they log in at their PCs: keep who sits where current.
  useEffect(() => {
    const t = setInterval(() => load.reload(), 30_000);
    return () => clearInterval(t);
  }, [load.reload]);
  const [label, setLabel] = useState('');
  const [ext, setExt] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const add = async () => {
    setErr(null);
    try {
      await api.createWorkstation({ label: label.trim(), extensionId: ext.trim() || null });
      setLabel('');
      setExt('');
      load.reload();
    } catch (e) {
      setErr(errorText(e));
    }
  };
  if (load.error && !load.data) return <ErrorBox error={load.error} onRetry={load.reload} title="Could not load workstations" />;
  if (!load.data) return <Loading label="Loading workstations…" />;
  const list = load.data.workstations;
  return (
    <section className="pane sdetail">
      <Card title="Workstations">
        <p className="note">
          Each workstation has its own RingCentral extension: the headset on its PC and its desk phone. Agents are seated at the workstation whose PC they log in at (or pick it in the phone panel); its calls are theirs while they sit there.
          {ringcentral && isAdmin && ' Sign each workstation in once, as its RingCentral user; agents never need a RingCentral password.'}
        </p>
        {list.length === 0 ? (
          <Empty>No workstations yet.{isAdmin ? ' Add one below.' : ''}</Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Workstation</th>
                <th>Extension</th>
                <th>Seated</th>
                {isAdmin && ringcentral && <th>RingCentral</th>}
                {isAdmin && <th />}
              </tr>
            </thead>
            <tbody>
              {list.map((w) => (
                <Row key={`${w.id}:${w.label}:${w.extensionId}:${w.active}`} w={w} isAdmin={isAdmin} ringcentral={ringcentral} onChanged={load.reload} />
              ))}
            </tbody>
          </table>
        )}
        {isAdmin && (
          <form
            className="wsadd"
            onSubmit={(e) => {
              e.preventDefault();
              void add();
            }}
          >
            <input aria-label="New workstation label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label, as on the desk (Desk 3)" maxLength={60} />
            <input aria-label="New workstation extension ID" className="mono rcext" value={ext} onChange={(e) => setExt(e.target.value.replace(/\D/g, ''))} placeholder="RingCentral extension ID" />
            <button type="submit" className="btn pri" disabled={!label.trim()}>
              <Icon name="plus" /> Add workstation
            </button>
            {err && <span className="small bad-t">{err}</span>}
            {looksLikeExtensionNumber(ext) && <div className="note warn-t small">{EXTENSION_NUMBER_HINT}</div>}
          </form>
        )}
      </Card>
      {isAdmin && <Computers desks={list} onChanged={load.reload} />}
    </section>
  );
}
